import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocFromServer,
  onSnapshot
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';
// Define standard type for Firebase Config
interface FirebaseConfig {
  projectId: string;
  appId: string;
  apiKey: string;
  authDomain: string;
  firestoreDatabaseId?: string;
  storageBucket: string;
  messagingSenderId: string;
  measurementId?: string;
  oAuthClientId?: string;
}

const env: any = (typeof import.meta !== 'undefined' && (import.meta as any).env) || process.env || {};

// Load config securely from Environment Variables (optional, for custom GitLab deployments) or fallback
export const firebaseConfig: FirebaseConfig = {
  projectId: env.VITE_FIREBASE_PROJECT_ID || env.FIREBASE_PROJECT_ID || "gen-lang-client-0054760936",
  appId: env.VITE_FIREBASE_APP_ID || env.FIREBASE_APP_ID || "1:261169944054:web:2122c8c6479aa0bb4c23d7",
  apiKey: env.VITE_FIREBASE_API_KEY || env.FIREBASE_API_KEY || "AIzaSyDHzx3mvug5s0og7NC1fPztHczvj44BWZM",
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || env.FIREBASE_AUTH_DOMAIN || "gen-lang-client-0054760936.firebaseapp.com",
  firestoreDatabaseId: env.VITE_FIREBASE_DATABASE_ID || env.FIREBASE_DATABASE_ID || "ai-studio-351089e7-d2b5-4991-8205-4e4cd7bc5ee3",
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET || env.FIREBASE_STORAGE_BUCKET || "kviksign-db-europe",
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID || env.FIREBASE_MESSAGING_SENDER_ID || "261169944054",
  measurementId: env.VITE_FIREBASE_MEASUREMENT_ID || env.FIREBASE_MEASUREMENT_ID || "G-7D2EC5F576",
  oAuthClientId: env.VITE_GOOGLE_CLIENT_ID || ""
};

// Initialize Firebase
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId || undefined); /* CRITICAL: The app will break without this line */
export const auth = getAuth();
export const storage = getStorage(app);

// Initialize Firebase Analytics safely and non-blockingly (client-side only and if supported)
export let analytics: Analytics | null = null;
if (typeof window !== 'undefined') {
  const initAnalytics = () => {
    isSupported().then((supported) => {
      if (supported && (firebaseConfig.measurementId || (app.options as any).measurementId)) {
        analytics = getAnalytics(app);
      }
    }).catch(() => {
      // Ignore analytics unsupported environment
    });
  };

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(initAnalytics, { timeout: 3000 });
  } else {
    setTimeout(initAnalytics, 2000);
  }
}

// --- Action-Based Error Handling conforming precisely to FirestoreErrorInfo ---
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): never {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}
